import {
  applyMove,
  autoBury,
  autoExchange,
  autoTrump,
  checkMove,
  chooseBury,
  chooseCard,
  chooseGive,
  chooseTrump,
  createMatch,
  makeRng,
  publicView,
  submitBury,
  submitExchange,
  submitTrump,
  timeoutMove,
  type CardId,
  type Match,
  type Move,
  type Seat,
  type Suit,
} from '@ubs/rules';
import {
  buildViewMessage,
  FALLBACK_CHAT_PHRASES,
  deriveEvents,
  eventDuration,
  type GameEvent,
  type PublicSeat,
} from '@ubs/protocol';
import { clearLocal, saveLocal, type SavedGame } from './localStore.js';
import type { Transport, TransportHandlers } from './transport.js';

/**
 * Botlara karşı yerel oyun — tarayıcıda, sunucusuz.
 *
 * Online masayla AYNI olay akışını ve aynı `ViewMessage` şeklini üretiyor
 * (`deriveEvents` / `buildViewMessage` paylaşılan pakette). Masa bileşeni
 * online mı yerel mi oynadığını bilmiyor.
 *
 * Bunun bir de ürün karşılığı var: uygulama internetsiz de oynanabiliyor,
 * yani App Store'un "web sitesi sarmalayıcısı" reddine düşmüyor.
 */

const HUMAN: Seat = 0;
const BOT_NAMES = ['Tansel', 'Efsane'];

export interface LocalGameOptions {
  name: string;
  /** Bot hamlesinden önceki bekleme — insanlar takip edebilsin. */
  botDelayMs?: number;
  /** İnsan oyuncunun kart atma süresi; 0 verilirse sayaç kapanır. */
  turnMs?: number;
  /** Koz seçimi süresi. */
  trumpMs?: number;
  /** Gömme süresi — 16 kart tartılıyor, hamleden uzun. */
  buryMs?: number;
  /** Kart alışverişi süresi. */
  exchangeMs?: number;
  /**
   * Masaya oturma ile kartların dağıtılması arasındaki geri sayım.
   * 0 verilirse hemen başlar.
   */
  startDelayMs?: number;
  /** Botlar ara sıra hazır mesaj yollasın mı? Testlerde kapatılıyor. */
  botChat?: boolean;
  /** Test ve tekrar oynatma için sabit tohum. */
  seed?: string;
  /** Yarım kalmış maç — verilirse oradan devam edilir, yeniden dağıtılmaz. */
  resume?: SavedGame | undefined;
}

/** Bot hamlesinden sonra mesaj yollama olasılığı. */
const BOT_CHAT_CHANCE = 0.12;
/** Aynı botun iki mesajı arasındaki en kısa süre. */
const BOT_CHAT_COOLDOWN_MS = 8000;

export function createLocalGame(
  options: LocalGameOptions,
  handlers: TransportHandlers,
): Transport {
  const botDelayMs = options.botDelayMs ?? 320;
  const turnMs = options.turnMs ?? 15_000;
  const trumpMs = options.trumpMs ?? 20_000;
  const buryMs = options.buryMs ?? 25_000;
  const exchangeMs = options.exchangeMs ?? 20_000;
  // 3000: geri sayım tam olarak 3-2-1 okunsun.
  const startDelayMs = options.startDelayMs ?? 3000;
  const botChat = options.botChat ?? true;
  const resume = options.resume;
  const seed = resume?.seed ?? options.seed ?? randomSeed();

  let match: Match = resume ? resume.match : createMatch(seed);
  let seq = 0;
  let deadline: number | null = null;
  let deadlineTotal: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** Başlangıç geri sayımı — sıra zamanlayıcısıyla çakışmasın diye ayrı. */
  let startTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;
  /**
   * Maç başladı mı — "oyuna başla" basılana kadar hiçbir zamanlayıcı
   * kurulmuyor. Devam eden maçta zaten başlamış sayılıyor.
   */
  let begun = resume !== undefined;
  const botMoves = resume ? [...resume.botMoves] : [0, 0, 0];
  /** Sohbet için ayrı akış: dağıtımın rastgeleliğini bozmasın. */
  const chatRng = makeRng(`${seed}#chat`);
  const lastBotChatAt = [0, 0, 0];

  const seats = (): PublicSeat[] =>
    ([0, 1, 2] as Seat[]).map((seat) => ({
      seat,
      name: seat === HUMAN ? options.name : BOT_NAMES[seat - 1]!,
      connected: true,
      bot: seat !== HUMAN,
      botMoves: botMoves[seat]!,
    }));

  const isBot = (seat: Seat) => seat !== HUMAN;

  function emit(events: GameEvent[]): void {
    if (disposed) return;
    seq += 1;
    for (const event of events) handlers.event(event);

    /**
     * Sıra, İSTEMCİ ANİMASYONU BİTTİKTEN SONRA başlıyor.
     *
     * İstemci bu olayları bir kuyrukta sırayla oynatıyor. Süre eklenmediğinde
     * bot gecikmesi kuyruğun ORTASINDA doluyordu: kartlar hâlâ uçarken bot
     * oynuyor, alışverişte gelen kartlar daha ekrana düşmeden sıradaki faz
     * başlıyordu. Sunucudaki masa da tam olarak bunu yapıyor.
     */
    const animation = events.reduce((total, event) => total + eventDuration(event), 0);
    schedule(animation); // deadline'ı belirliyor, bu yüzden görünümden önce
    handlers.view(
      buildViewMessage(match, HUMAN, { seq, seats: seats(), deadline, deadlineTotal }),
    );
  }

  function commit(next: Match): void {
    const events = deriveEvents(match, next);
    match = next;
    persist();
    emit(events);
  }

  /**
   * Durumu diske yazar. HER hamlede yazılıyor: uygulama arka planda
   * öldürülürse son yazılan hâlden devam ediliyor. Maliyeti bir JSON
   * serileştirmesi — hamle başına, sorun değil.
   */
  function persist(): void {
    if (match.phase === 'matchOver') {
      clearLocal();
      return;
    }
    saveLocal({ name: options.name, seed, match, botMoves });
  }

  function clearTimer(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  /** Geri sayımı kurar. Süre 0 ise (testler) hiç gösterilmez. */
  function countdown(ms: number): void {
    if (ms <= 0) return;
    deadline = Date.now() + ms;
    deadlineTotal = ms;
  }

  /**
   * Şu an kimden hamle bekleniyor.
   *
   * Dört faz da tek bir "sıra" kavramına indirgeniyor: koz ve gömme 8'ciye,
   * alışveriş kuyruğun başındaki isteyene, oyun ise sırası gelene ait.
   * Zamanlayıcı bu tek noktadan kuruluyor.
   */
  function actor(): Seat | null {
    if (match.phase === 'matchOver') return null;
    if (match.phase === 'exchange') return match.exchange!.queue[0]?.from ?? null;
    if (match.phase === 'trump' || match.phase === 'bury') return match.deal!.role8;
    return match.deal!.turn;
  }

  function humanMs(): number {
    switch (match.phase) {
      case 'trump':
        return trumpMs;
      case 'bury':
        return buryMs;
      case 'exchange':
        return exchangeMs;
      default:
        return turnMs;
    }
  }

  /** `animationMs`: istemcinin oynatmakta olduğu animasyonun toplam süresi. */
  function schedule(animationMs = 0): void {
    clearTimer();
    deadline = null;
    deadlineTotal = null;
    if (disposed || !begun) return;

    const seat = actor();
    if (seat === null) return;

    if (isBot(seat)) {
      // Botun düşünme süresi de geri sayım olarak gidiyor: rakip avatarında
      // halka dönsün, masa donmuş görünmesin.
      const ms = botDelayMs + animationMs;
      countdown(ms);
      timer = setTimeout(() => playAuto(seat, true), ms);
      return;
    }

    const ms = humanMs() + animationMs;
    if (ms > 0) {
      countdown(ms);
      timer = setTimeout(() => playAuto(seat, false), ms);
    }
  }

  /**
   * Bir koltuk adına otomatik oynar.
   *
   * `asBot` true ise bot mantığı, false ise SÜRE AŞIMI varsayılanı çalışıyor.
   * İkisi bilerek farklı: süresi dolan insan oyuncu adına en zararsız hamle
   * yapılıyor (en düşük geçerli kart, en uzun renk koz, en düşükleri göm),
   * bot ise gerçekten oynuyor.
   */
  function playAuto(seat: Seat, asBot: boolean): void {
    if (disposed) return;
    const view = publicView(match, seat);

    try {
      switch (match.phase) {
        case 'exchange':
          if (asBot) botMoves[seat] = (botMoves[seat] ?? 0) + 1;
          commit(
            asBot
              ? submitExchange(match, seat, chooseGive(view))
              : autoExchange(match, seat),
          );
          break;
        case 'trump':
          if (asBot) botMoves[seat] = (botMoves[seat] ?? 0) + 1;
          commit(asBot ? submitTrump(match, seat, chooseTrump(view)) : autoTrump(match, seat));
          break;
        case 'bury':
          if (asBot) botMoves[seat] = (botMoves[seat] ?? 0) + 1;
          commit(asBot ? submitBury(match, seat, chooseBury(view)) : autoBury(match, seat));
          break;
        case 'play': {
          if (asBot) botMoves[seat] = (botMoves[seat] ?? 0) + 1;
          const move: Move = asBot
            ? { type: 'play', card: chooseCard(view, 'normal') }
            : timeoutMove(match, seat);
          commit(applyMove(match, seat, move));
          if (asBot) maybeBotChat(seat);
          break;
        }
        default:
          return;
      }
    } catch (cause) {
      // Yerel oyunda kural motoru hatası masayı dondurmamalı; en azından
      // konsola düşsün ve oyun kilitlenmeden dursun.
      console.error('Otomatik hamle başarısız:', cause);
    }
  }

  /**
   * Botlar ara sıra hazır mesaj yollar — masa sessiz kalmasın ve balonların
   * nerede çıktığı gerçek oyunda da görünsün.
   *
   * Sohbet KUYRUĞA GİRMİYOR: `useGame` `chat` olayını kuyruktan önce yakalıyor,
   * yani mesaj kart animasyonlarının arkasında beklemiyor.
   */
  function maybeBotChat(seat: Seat): void {
    if (!botChat || disposed || seat === HUMAN) return;
    if (chatRng() > BOT_CHAT_CHANCE) return;
    const now = Date.now();
    if (now - lastBotChatAt[seat]! < BOT_CHAT_COOLDOWN_MS) return;
    lastBotChatAt[seat] = now;
    const text = FALLBACK_CHAT_PHRASES[Math.floor(chatRng() * FALLBACK_CHAT_PHRASES.length)];
    if (text) handlers.event({ kind: 'chat', seat, text });
  }

  function begin(): void {
    if (disposed) return;
    startTimer = null;
    begun = true;
    persist();
    emit([
      { kind: 'matchStart', seats: seats() },
      { kind: 'dealStart', dealIndex: 0, role8: match.deal!.role8 },
    ]);
  }

  /**
   * Önce MASA görünür, sonra kartlar dağıtılır. Maç anında başlasaydı olay
   * kuyruğu oynarken ekranda hiçbir şey olmazdı — "botlarla oyna" tıklanıyor,
   * bir süre bekleniyor, sonra masa beliriyordu.
   */
  queueMicrotask(() => {
    if (disposed) return;
    handlers.lobby(
      seats().map((s) => s.name),
      0,
    );

    if (resume) {
      /**
       * Yarım kalmış maç: geri sayım da "oyuna başla" da yok, masa olduğu
       * gibi açılıyor. `matchStart` yalnızca istemciyi "oynanıyor" durumuna
       * geçirmek için — kartlar zaten elde.
       */
      handlers.event({ kind: 'matchStart', seats: seats() });
      seq += 1;
      schedule();
      handlers.view(
        buildViewMessage(match, HUMAN, { seq, seats: seats(), deadline, deadlineTotal }),
      );
      return;
    }

    /**
     * Masa HEMEN görünüyor ama oyun kendiliğinden BAŞLAMIYOR: ortadaki
     * "oyuna başla" düğmesine basılınca geri sayım dönüyor. Botlara karşı
     * oynarken beklenecek kimse yok, o yüzden başlama anını oyuncu seçiyor.
     *
     * El BOŞ gönderiliyor: kartlar sayım bitip `dealStart` oynadığında
     * dağıtılıyor, yoksa oyuncu daha sayım başlamadan eline bakmış oluyor.
     */
    handlers.view({
      ...buildViewMessage(match, HUMAN, {
        seq: 0,
        seats: seats(),
        deadline: null,
        deadlineTotal: null,
      }),
      hand: [],
      handCounts: [0, 0, 0],
    });
  });

  /** "Oyuna başla" — geri sayımı kurar. İkinci kez basmak etkisiz. */
  function requestStart(): void {
    if (disposed || begun || startTimer !== null) return;
    if (startDelayMs <= 0) {
      begin();
      return;
    }
    handlers.starting(Date.now() + startDelayMs);
    startTimer = setTimeout(begin, startDelayMs);
  }

  const fail = (cause: unknown, fallback: string) =>
    handlers.error(cause instanceof Error ? cause.message : fallback);

  return {
    send(type, payload) {
      if (disposed) return;
      try {
        switch (type) {
          case 'move': {
            const move = payload as Move;
            const error = checkMove(match, HUMAN, move);
            if (error) {
              handlers.error(error);
              return;
            }
            commit(applyMove(match, HUMAN, move));
            return;
          }
          case 'trump':
            commit(submitTrump(match, HUMAN, (payload as { suit: Suit }).suit));
            return;
          case 'bury':
            commit(submitBury(match, HUMAN, (payload as { cards: CardId[] }).cards));
            return;
          case 'exchange':
            commit(
              submitExchange(
                match,
                HUMAN,
                (payload as { gives: { to: Seat; cards: CardId[] }[] }).gives,
              ),
            );
            return;
          case 'chat': {
            // Yerel modda karşı taraf yok; mesaj doğrudan kendi ekranına
            // dönüyor. Aynı olay şekli kullanılıyor ki masa bileşeni modu
            // bilmesin.
            const { text } = payload as { text: string };
            if (FALLBACK_CHAT_PHRASES.includes(text)) {
              handlers.event({ kind: 'chat', seat: HUMAN, text });
            }
            return;
          }
          case 'start':
            requestStart();
            return;
          case 'hello':
            return; // yerel oyunda el sıkışma yok
        }
      } catch (cause) {
        fail(cause, 'Geçersiz istek');
      }
    },
    dispose() {
      disposed = true;
      clearTimer();
      if (startTimer !== null) clearTimeout(startTimer);
      startTimer = null;
    },
  };
}

function randomSeed(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `local-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}
